import { useState } from "react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { STREAMING_PLATFORMS } from "@/constants/platforms";
import { v4 as uuid } from "@/lib/uuid";

export function PlatformLinksEditor({ links, onChange }) {
  const [dragIndex, setDragIndex] = useState(null);
  const [overIndex, setOverIndex] = useState(null);

  const addLink = () => {
    onChange([...links, { id: uuid(), platform: "spotify", url: "", cta_label: "Play", order: links.length }]);
  };

  const updateLink = (id, patch) => {
    onChange(links.map((l) => (l.id === id ? { ...l, ...patch } : l)));
  };

  const removeLink = (id) => {
    onChange(links.filter((l) => l.id !== id));
  };

  const handleDrop = (dropIndex) => {
    if (dragIndex === null || dragIndex === dropIndex) {
      setDragIndex(null);
      setOverIndex(null);
      return;
    }
    const reordered = [...links];
    const [moved] = reordered.splice(dragIndex, 1);
    reordered.splice(dropIndex, 0, moved);
    onChange(reordered.map((l, idx) => ({ ...l, order: idx })));
    setDragIndex(null);
    setOverIndex(null);
  };

  return (
    <div className="space-y-3" data-testid="platform-links-editor">
      {links.map((link, i) => (
        <div
          key={link.id}
          onDragOver={(e) => { e.preventDefault(); setOverIndex(i); }}
          onDrop={() => handleDrop(i)}
          className={`flex items-center gap-2 rounded-xl border p-3 transition-opacity ${overIndex === i && dragIndex !== null && dragIndex !== i ? "border-indigo-400" : "border-white/10"} ${dragIndex === i ? "opacity-40" : ""} bg-white/[0.03]`}
          data-testid={`platform-link-row-${link.id}`}
        >
          <span
            draggable
            onDragStart={(e) => { setDragIndex(i); e.dataTransfer.effectAllowed = "move"; }}
            onDragEnd={() => { setDragIndex(null); setOverIndex(null); }}
            className="cursor-grab active:cursor-grabbing text-white/30 hover:text-white/60 shrink-0"
            data-testid={`platform-link-drag-handle-${link.id}`}
          >
            <i className="fa-solid fa-grip-vertical text-xs" />
          </span>
          <Select value={link.platform} onValueChange={(v) => updateLink(link.id, { platform: v })}>
            <SelectTrigger className="w-40 bg-transparent border-white/15" data-testid={`platform-select-${link.id}`}>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {STREAMING_PLATFORMS.map((p) => (
                <SelectItem key={p.value} value={p.value}>
                  <i className={`${p.icon} mr-2`} />{p.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Input
            value={link.url}
            onChange={(e) => updateLink(link.id, { url: e.target.value })}
            placeholder="https://..."
            className="flex-1 bg-transparent border-white/15"
            data-testid={`platform-url-input-${link.id}`}
          />
          <Input
            value={link.cta_label ?? "Play"}
            onChange={(e) => updateLink(link.id, { cta_label: e.target.value })}
            placeholder="Play"
            className="w-24 shrink-0 bg-transparent border-white/15 text-center"
            title="Button CTA text (default: Play)"
            data-testid={`platform-cta-input-${link.id}`}
          />
          <button onClick={() => removeLink(link.id)} className="shrink-0 p-2 text-white/40 hover:text-rose-400" data-testid={`remove-platform-link-${link.id}`}>
            <i className="fa-solid fa-trash text-xs" />
          </button>
        </div>
      ))}
      <Button type="button" variant="outline" onClick={addLink} className="border-white/15 border-dashed hover:bg-white/10 w-full" data-testid="add-platform-link-button">
        <i className="fa-solid fa-plus mr-2" /> Add Platform Link
      </Button>
    </div>
  );
}
